/**
 * Teacher Profile API endpoints
 */

import apiClient from "../apiClient";
import { ApiResponse } from "@/types/api.types";
import {
  TeacherProfile,
  TeacherProfileUpdatePayload,
  PhotoUploadResponse,
} from "@/types/teacher-profile.types";
import { logger } from "@/utils/logger"
/**
 * Get teacher profile
 */
export const getTeacherProfile = async (): Promise<TeacherProfile> => {
  logger.log("🌐 [API] GET /teacher/profile");
  try {
    const response = await apiClient.get<TeacherProfile>("/teacher/profile");
    logger.log("✅ [API] Profile response:", response);
    logger.log("✅ [API] Profile response type:", typeof response);
    logger.log(
      "✅ [API] Profile response keys:",
      response ? Object.keys(response) : "null/undefined"
    );

    if (!response) {
      logger.error("❌ [API] Profile response is null/undefined!");
      throw new Error("Profile endpoint returned no data");
    }

    return response;
  } catch (error: any) {
    logger.error("❌ [API] GET profile error:", error);
    throw error;
  }
};

/**
 * Update teacher profile
 */
export const updateTeacherProfile = async (
  data: TeacherProfileUpdatePayload
): Promise<TeacherProfile> => {
  logger.log("🌐 [API] PUT /teacher/profile with data:", data);
  try {
    const response = await apiClient.put<ApiResponse<TeacherProfile>>(
      "/teacher/profile",
      data
    );
    logger.log("✅ [API] Update response:", response);

    // Backend retorna apenas "User Updated Successfully"
    // Precisamos fazer GET para pegar o profile atualizado
    logger.log("🔄 [API] Fetching updated profile after PUT...");
    const updatedProfile = await getTeacherProfile();
    logger.log("✅ [API] Got updated profile:", updatedProfile);

    return updatedProfile;
  } catch (error: any) {
    logger.error("❌ [API] Update error:", {
      status: error.status,
      message: error.message,
      errors: error.errors,
      responseData: error.response?.data,
      responseStatus: error.response?.status,
    });

    // Se for erro 422 (validação), extrair detalhes
    if (error.status === 422 && error.errors) {
      logger.error("❌ [API] Validation errors:", error.errors);
    }

    throw error;
  }
};

/**
 * Upload profile photo
 */
export const uploadProfilePhoto = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  logger.log("🌐 [API] POST /teacher/profile/photo");
  const formData = new FormData();
  formData.append("photo", file);

  try {
    const response = await apiClient.upload<ApiResponse<PhotoUploadResponse>>(
      "/teacher/profile/photo",
      formData,
      (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      }
    );

    logger.log("✅ [API] Upload response:", response);
    logger.log("✅ [API] Upload response type:", typeof response);

    // Backend retorna apenas "Photo uploaded successfully"
    // Precisamos fazer GET para pegar a URL atualizada
    logger.log("🔄 [API] Fetching updated profile to get photo URL...");
    const updatedProfile = await getTeacherProfile();

    if (updatedProfile?.photo) {
      logger.log("✅ [API] Got photo URL from profile:", updatedProfile.photo);
      return updatedProfile.photo;
    }

    logger.error("❌ [API] Updated profile has no photo URL");
    throw new Error("Photo uploaded but URL not found in profile");
  } catch (error: any) {
    logger.error("❌ [API] Upload photo error:", error);
    throw error;
  }
};
